import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Plus, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Textarea } from '../../../components/ui/textarea';
import FocalPointSelector from '../../../components/FocalPointSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminCursoFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    slug: '',
    descripcion_corta: '',
    descripcion_larga: '',
    programa: '',
    docentes: '',
    instructores: [],
    imagen: '',
    imagen_focal_point: { x: 50, y: 50 },
    modalidad: '',
    lugar: '',
    fecha: '',
    fechas: [],
    hora: '',
    horario_a_coordinar: false,
    duracion_horas: '',
    campo_extra_nombre: '',
    tiene_examen: false,
    tipo_curso: 'CURSO',
    tipo_curso_otro: '',
    activo: true,
    min_jornadas_acreditacion: ''
  });

  useEffect(() => {
    if (isEditing) {
      fetchCurso();
    }
  }, [id]);

  const fetchCurso = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/admin/cursos/${id}`);
      const curso = res.data;
      // Convertir fecha legacy a array si no hay fechas
      let fechasArray = curso.fechas || [];
      if (fechasArray.length === 0 && curso.fecha) {
        fechasArray = [curso.fecha.split('T')[0]];
      }
      setFormData({
        nombre: curso.nombre || '',
        slug: curso.slug || '',
        descripcion_corta: curso.descripcion_corta || '',
        descripcion_larga: curso.descripcion_larga || '',
        programa: curso.programa || '',
        docentes: curso.docentes || '',
        instructores: curso.instructores || [],
        imagen: curso.imagen || '',
        imagen_focal_point: curso.imagen_focal_point || { x: 50, y: 50 },
        modalidad: curso.modalidad || '',
        lugar: curso.lugar || '',
        fecha: curso.fecha ? curso.fecha.split('T')[0] : '',
        fechas: fechasArray.map(f => f.split('T')[0]),
        hora: curso.hora || '',
        horario_a_coordinar: curso.horario_a_coordinar || false,
        duracion_horas: curso.duracion_horas || '',
        campo_extra_nombre: curso.campo_extra_nombre || '',
        tiene_examen: curso.tiene_examen || false,
        tipo_curso: curso.tipo_curso || 'CURSO',
        tipo_curso_otro: curso.tipo_curso_otro || '',
        activo: curso.activo !== false,
        min_jornadas_acreditacion: curso.min_jornadas_acreditacion != null ? String(curso.min_jornadas_acreditacion) : ''
      });
    } catch (err) {
      setError('Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Manejo de instructores
  const handleAddInstructor = () => {
    setFormData(prev => ({
      ...prev,
      instructores: [...prev.instructores, { nombre: '', aparece_en_certificado: true }]
    }));
  };

  const handleRemoveInstructor = (index) => {
    setFormData(prev => ({
      ...prev,
      instructores: prev.instructores.filter((_, i) => i !== index)
    }));
  };

  const handleInstructorChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      instructores: prev.instructores.map((inst, i) => 
        i === index ? { ...inst, [field]: value } : inst
      )
    }));
  };

  // Manejo de múltiples fechas
  const handleAddDate = () => {
    setFormData(prev => ({
      ...prev,
      fechas: [...prev.fechas, '']
    }));
  };

  const handleRemoveDate = (index) => {
    setFormData(prev => ({
      ...prev,
      fechas: prev.fechas.filter((_, i) => i !== index)
    }));
  };

  const handleDateChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      fechas: prev.fechas.map((f, i) => i === index ? value : f)
    }));
  };

  // Subida de imagen
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await axios.post(`${API_URL}/admin/upload-imagen-curso`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleChange('imagen', res.data.url);
    } catch (err) {
      setError('Error al subir la imagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    // Normalizar min_jornadas_acreditacion: vacío → null, número → int
    const payload = {
      ...formData,
      min_jornadas_acreditacion:
        formData.min_jornadas_acreditacion === '' || formData.min_jornadas_acreditacion == null
          ? null
          : Number(formData.min_jornadas_acreditacion)
    };

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/capacitaciones/admin/cursos/${id}`, payload);
      } else {
        await axios.post(`${API_URL}/capacitaciones/admin/cursos`, payload);
      }
      navigate('/admin/capacitaciones/cursos');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/capacitaciones/cursos">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Curso' : 'Nuevo Curso'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Información básica</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="tipo_curso">Tipo de actividad *</Label>
                    <select
                      id="tipo_curso"
                      value={formData.tipo_curso}
                      onChange={(e) => handleChange('tipo_curso', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    >
                      <option value="CURSO">Curso</option>
                      <option value="CONVERSATORIO">Conversatorio</option>
                      <option value="SEMINARIO">Seminario</option>
                      <option value="TALLER">Taller</option>
                      <option value="CAPACITACION">Capacitación</option>
                      <option value="TUTORIA">Tutoría 1 a 1</option>
                      <option value="OTRO">Otro</option>
                    </select>
                  </div>
                  {formData.tipo_curso === 'OTRO' && (
                    <div>
                      <Label htmlFor="tipo_curso_otro">Especificar tipo *</Label>
                      <Input
                        id="tipo_curso_otro"
                        value={formData.tipo_curso_otro}
                        onChange={(e) => handleChange('tipo_curso_otro', e.target.value)}
                        placeholder="Ej: Jornada, Congreso, etc."
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Intervención en crisis"
                  />
                </div>

                <div>
                  <Label htmlFor="slug">Link personalizado <span className="text-gray-400">(opcional)</span></Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500 whitespace-nowrap">.../capacitaciones/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => {
                        // Solo minúsculas, números y guiones
                        const v = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, '-')
                          .replace(/[^a-z0-9-]/g, '');
                        handleChange('slug', v);
                      }}
                      placeholder={isEditing ? formData.slug : "intervencion-crisis-2026"}
                      data-testid="curso-slug-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {isEditing
                      ? 'Este link es independiente del nombre: cambiá solo si querés re-direccionar. Pierdes acceso al link anterior.'
                      : 'Si lo dejás vacío se genera del nombre. Solo minúsculas, números y guiones.'}
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="descripcion_corta">Descripción corta</Label>
                  <Input
                    id="descripcion_corta"
                    value={formData.descripcion_corta}
                    onChange={(e) => handleChange('descripcion_corta', e.target.value)}
                    placeholder="Breve descripción para el catálogo"
                  />
                </div>
                
                <div>
                  <Label htmlFor="descripcion_larga">Descripción completa</Label>
                  <Textarea
                    id="descripcion_larga"
                    value={formData.descripcion_larga}
                    onChange={(e) => handleChange('descripcion_larga', e.target.value)}
                    placeholder="Descripción detallada del curso"
                    rows={4}
                  />
                </div>
                
                <div>
                  <Label htmlFor="programa">Programa / Contenidos</Label>
                  <Textarea
                    id="programa"
                    value={formData.programa}
                    onChange={(e) => handleChange('programa', e.target.value)}
                    placeholder="Detalle de los temas a tratar"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Instructores */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Instructores / Docentes</span>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddInstructor}>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.instructores.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No hay instructores. En el certificado dirá "dictado por el Colegio Profesional de Trabajadorxs Sociales del Distrito Lomas de Zamora".
                  </p>
                ) : (
                  <div className="space-y-3">
                    {formData.instructores.map((instructor, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Input
                              value={instructor.nombre}
                              onChange={(e) => handleInstructorChange(index, 'nombre', e.target.value)}
                              placeholder="Nombre del instructor (ej: Lic. María García)"
                            />
                          </div>
                          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={instructor.aparece_en_certificado}
                              onChange={(e) => handleInstructorChange(index, 'aparece_en_certificado', e.target.checked)}
                              className="rounded"
                            />
                            En certificado
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveInstructor(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div>
                          <Input
                            value={instructor.descripcion || ''}
                            onChange={(e) => handleInstructorChange(index, 'descripcion', e.target.value)}
                            placeholder="Breve descripción (ej: Trabajadora Social, Especialista en...)"
                            className="text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Solo los instructores marcados con "En certificado" aparecerán en el documento. La descripción se mostrará en la página pública del curso.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Detalles del evento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="modalidad">Modalidad</Label>
                    <select
                      id="modalidad"
                      value={formData.modalidad}
                      onChange={(e) => handleChange('modalidad', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Presencial">Presencial</option>
                      <option value="Virtual">Virtual</option>
                      <option value="Híbrida">Híbrida</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="duracion_horas">Duración (horas cátedra)</Label>
                    <Input
                      id="duracion_horas"
                      type="number"
                      value={formData.duracion_horas}
                      onChange={(e) => handleChange('duracion_horas', parseInt(e.target.value) || '')}
                      placeholder="Ej: 8"
                    />
                  </div>
                </div>

                {/* Lugar - solo si modalidad es Presencial o Híbrida */}
                {(formData.modalidad === 'Presencial' || formData.modalidad === 'Híbrida') && (
                  <div>
                    <Label htmlFor="lugar">Lugar (dirección o sede)</Label>
                    <Input
                      id="lugar"
                      value={formData.lugar}
                      onChange={(e) => handleChange('lugar', e.target.value)}
                      placeholder="Ej: Sede del Colegio, Gorriti 366, Lomas de Zamora"
                      data-testid="curso-lugar-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Aparece en la card y el detalle del curso.</p>
                  </div>
                )}

                {/* Checkbox horario a coordinar */}
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <input
                    type="checkbox"
                    id="horario_a_coordinar"
                    checked={formData.horario_a_coordinar}
                    onChange={(e) => handleChange('horario_a_coordinar', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="horario-a-coordinar-checkbox"
                  />
                  <Label htmlFor="horario_a_coordinar" className="text-sm font-medium text-blue-800 cursor-pointer">
                    📅 Fecha/horario a coordinar (tutorías 1 a 1, cursos a demanda)
                  </Label>
                </div>

                {/* Múltiples fechas - solo mostrar si NO es a coordinar */}
                {!formData.horario_a_coordinar && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Fechas del evento</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={handleAddDate}
                      data-testid="btn-agregar-fecha"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar fecha
                    </Button>
                  </div>
                  {formData.fechas.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-3 bg-gray-50 rounded-md">
                      No hay fechas. Hacé clic en "Agregar fecha" para añadir.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {formData.fechas.map((fecha, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            type="date"
                            value={fecha}
                            onChange={(e) => handleDateChange(index, e.target.value)}
                            className="flex-1"
                            data-testid={`fecha-input-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveDate(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`btn-eliminar-fecha-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Las fechas aparecerán en el certificado separadas por comas o con "y" para la última.
                  </p>
                </div>
                )}

                {/* Hora del evento - solo mostrar si NO es a coordinar */}
                {!formData.horario_a_coordinar && (
                <div>
                  <Label htmlFor="hora">Horario</Label>
                  <Input
                    id="hora"
                    type="time"
                    value={formData.hora}
                    onChange={(e) => handleChange('hora', e.target.value)}
                    className="w-full"
                    data-testid="hora-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Hora de inicio del evento (ej: 18:00)
                  </p>
                </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Configuración adicional</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="campo_extra_nombre">Campo extra en inscripción (opcional)</Label>
                  <Input
                    id="campo_extra_nombre"
                    value={formData.campo_extra_nombre}
                    onChange={(e) => handleChange('campo_extra_nombre', e.target.value)}
                    placeholder="Ej: Institución donde trabaja"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si completás este campo, aparecerá en el formulario de inscripción
                  </p>
                </div>

                <div className="flex items-center gap-2 pt-4 border-t">
                  <input
                    type="checkbox"
                    id="tiene_examen"
                    checked={formData.tiene_examen}
                    onChange={(e) => handleChange('tiene_examen', e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="tiene_examen" className="cursor-pointer">
                    El curso tiene examen final
                  </Label>
                </div>
                {formData.tiene_examen && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    Los certificados incluirán la calificación obtenida por cada participante
                  </p>
                )}

                {formData.fechas && formData.fechas.length > 1 && (
                  <div className="pt-4 border-t" data-testid="min-jornadas-block">
                    <Label htmlFor="min_jornadas_acreditacion">
                      Mínimo de clases para acreditar el curso
                    </Label>
                    <Input
                      id="min_jornadas_acreditacion"
                      type="number"
                      min={1}
                      max={formData.fechas.length}
                      value={formData.min_jornadas_acreditacion}
                      onChange={(e) => handleChange('min_jornadas_acreditacion', e.target.value)}
                      placeholder={`Por defecto: ${formData.fechas.length} (todas)`}
                      className="mt-1"
                      data-testid="min-jornadas-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Este curso tiene <strong>{formData.fechas.length} clases</strong>.
                      Dejá en blanco si querés que se requieran <strong>todas</strong> para emitir certificado.
                      Si ponés un número menor (ej: 3 de 4), el sistema enviará certificados o los incluirá en el ZIP a quienes hayan asistido a esa cantidad de clases.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Imagen del curso</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Subir imagen (JPG, PNG)</Label>
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => {
                      handleImageUpload(e.target.files[0]);
                      // Reset focal point al subir nueva imagen
                      handleChange('imagen_focal_point', { x: 50, y: 50 });
                    }}
                    disabled={uploadingImage}
                    className="mt-1"
                  />
                  {uploadingImage && (
                    <p className="text-xs text-blue-600 mt-1">Subiendo imagen...</p>
                  )}
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">o pegar URL</span>
                  </div>
                </div>
                
                <Input
                  value={formData.imagen}
                  onChange={(e) => {
                    handleChange('imagen', e.target.value);
                    handleChange('imagen_focal_point', { x: 50, y: 50 });
                  }}
                  placeholder="https://..."
                />

                {formData.imagen && (
                  <div className="border-t pt-4">
                    <FocalPointSelector
                      imageUrl={formData.imagen}
                      focalPoint={formData.imagen_focal_point || { x: 50, y: 50 }}
                      onChange={(fp) => handleChange('imagen_focal_point', fp)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        handleChange('imagen', '');
                        handleChange('imagen_focal_point', { x: 50, y: 50 });
                      }}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Quitar imagen
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Estado</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={formData.activo}
                    onChange={(e) => handleChange('activo', e.target.checked)}
                    className="rounded"
                  />
                  <Label htmlFor="activo" className="cursor-pointer">
                    Curso activo (visible en catálogo)
                  </Label>
                </div>
              </CardContent>
            </Card>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={saving}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear curso')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
